import {ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {FangDropdownConfig} from './fang-dropdown.component.model';
import {sortBy} from '../../core/utils/fang-comp-lib.utils';

const dropdownTypeMap = {
	'default': 'dropdown-wrapper',
	'compact': 'dropdown-wrapper-sm',
	'large': 'dropdown-wrapper-lg'
}

@Component({
	selector: 'fang-dropdown',
	styleUrls: ['./fang-dropdown.component.scss'],
	templateUrl: './fang-dropdown.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class FangDropdownComponent implements OnChanges {
	@Input() public data: any[];
	@Input() public config: FangDropdownConfig = {};

	@Output() public selectedItemsChanged = new EventEmitter<any[]>();

	public dropdownData: string[];
	public filteredDropdownData: string[];
	public isDropdownOpen: boolean = false;
	public selectedItems: any[] = [];
	public searchInputText: string = '';

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['data']) {

			if (this.config.hasChipGroup && this.config.chipGroupConfig) {
				this.config.chipGroupConfig.propertyAccessKey = this.config.propertyAccessKey;
				this.config.chipGroupConfig.interaction = 'close';
			}

			// Transform dropdownData to string[]
			this.dropdownData = [...this.data].map((el: any) => {
				return this.getPropertyValue(el);
			});

			this.filteredDropdownData = [...this.dropdownData];

			this.filteredDropdownData = this.sortData(this.filteredDropdownData);
		}

		if (changes['config'].isFirstChange()) {
			if (!this.config.type) {
				this.config.type = 'default';
			}

			if (this.config.isGrowable === undefined) {
				this.config.isGrowable = true;
			}

			if (this.config.isPortfolio) {
				this.config.hasContainer = false;
				this.config.chipGroupConfig.alignment = 'group-inline';
				this.config.chipGroupConfig.chipBackground = undefined;
			}
		}

	}

	private selectItem(item: string): void {
		if (this.config.hasMultiSelect) {
			const isElementSelected: boolean = this.selectedItems.find((el: any) => this.getPropertyValue(el) === item);

			if (!isElementSelected) {
				this.selectedItems = [...this.selectedItems, this.data.find((el: any) => this.getPropertyValue(el) === item)];
			} else {
				this.selectedItems = this.selectedItems.filter((el: any) => this.getPropertyValue(el) !== item);
			}

			// Chip Group & portfolio
			if (this.config.hasChipGroup && this.config.chipGroupConfig) {
				this.dropdownData = this.dropdownData.filter((el: string) => el !== item);
				this.filteredDropdownData = this.filteredDropdownData.filter((el: string) => el !== item);
				this.filteredDropdownData = this.sortData(this.filteredDropdownData);
			}
			this.selectedItemsChanged.emit(this.selectedItems);

			// TODO Tree implementation

		} else { // single select
			this.selectedItems = [this.data.find((el: any) => this.getPropertyValue(el) === item)];
			this.isDropdownOpen = false;
			this.selectedItemsChanged.emit(this.selectedItems);
		}
	}

	public toggleDropdown(arrowPressed: boolean = false, event: MouseEvent = undefined): void {
		event && event.stopPropagation();

		if (arrowPressed || !this.config.hasChipGroup || (this.config.hasChipGroup && !this.selectedItems.length && !arrowPressed)) {
			this.isDropdownOpen = !this.isDropdownOpen;
		}
	}

	public getPropertyValue(element: any): string {
		return this.config.propertyAccessKey
			? element[this.config.propertyAccessKey]
			: element;
	}

	public isItemSelected(item: string): boolean {
		return this.selectedItems.find((el: any) => this.getPropertyValue(el) === item);
	}

	public removeChip(item: any): void {
		this.selectedItems = this.selectedItems.filter((el: any) => el !== item);

		// re-add chip to list only if its not portfolio
		if (this.config.hasChipGroup && this.config.chipGroupConfig) {
			this.dropdownData = [...this.dropdownData, this.getPropertyValue(item)];
			this.filteredDropdownData = [...this.filteredDropdownData, this.getPropertyValue(item)];
			this.filteredDropdownData = this.sortData(this.filteredDropdownData);
		}
	}

	public getConcatSelectedItems(items: any): string {
		return items.map(item => this.getPropertyValue(item)).join(', ');
	}

	public selectAll(): void {
		this.selectedItems = [...this.data];

		if (this.config.hasChipGroup) {
			this.dropdownData = [];
			this.filteredDropdownData = [];

		} else {
			this.dropdownData = this.selectedItems.map((selectedItem: any) => {
				return this.getPropertyValue(selectedItem);
			});
		}
	}

	public clearAll(): void {
		this.selectedItems = [];

		if (this.config.hasChipGroup) {
			this.dropdownData = [...this.data].map((dataEl: any) => {
				return this.getPropertyValue(dataEl);
			});

			this.filteredDropdownData = [...this.dropdownData];
			this.filteredDropdownData = this.sortData(this.filteredDropdownData);
		}
	}

	public proceedToSelection(item: string, event: MouseEvent = undefined): void {
		event && event.stopPropagation();

		if (!!this.selectedItems.find((el: any) => this.getPropertyValue(el) === item)
			|| (this.config.multiActions && this.config.multiActions.selectLimit && this.config.multiActions.selectLimit > this.selectedItems.length)
			|| (this.config.multiActions && !this.config.multiActions.selectLimit)
			|| !this.config.multiActions
		) {
			this.selectItem(item);
		}
	}

	private sortData(data: string[]): string[] {
		return sortBy(data);
	}

	public getDropdownSize(): string {
		return dropdownTypeMap[this.config.type];
	}

	public totalSelectedItems(): string {
		if (this.config.titleConfig && this.config.titleConfig.isTitleConcatenated) {
			if (this.config.multiActions && this.config.multiActions.selectLimit) {
				return `${this.getConcatSelectedItems(this.selectedItems)} (${this.selectedItems.length}/${this.config.multiActions.selectLimit})`
			}
			return `${this.getConcatSelectedItems(this.selectedItems)} (${this.selectedItems.length})`
		} else {
			if (this.config.multiActions && this.config.multiActions.selectLimit) {
				return `Total Selected (${this.selectedItems.length}/${this.config.multiActions.selectLimit})`
			}
			return `Total Selected (${this.selectedItems.length})`
		}
	}
}
